// src/components/DetailsPage.js
import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Carousel, Modal, Button } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./DetailsPage.css";

import LoadingComponent from "./LoadingComponent";
import CoreCompetencyModal from "./CoreCompetencyModal";
import FailedWoDetailsModal from "./FailedWoDetailsModal";
import CreditHoursModal from "./CreditHoursModal";
import WorkOrderDetailsModal from "./WorkOrderDetailsModal";
import "./CreditHoursModal.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const DetailsPage = ({ trainerID, isManager, isAdmin, isTrainer, isSpear }) => {
	const { alias } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const API = process.env.REACT_APP_API_URL;

	const authHeaders = useMemo(
		() => ({
			"x-trainer-id": trainerID,
		}),
		[trainerID],
	);

	const [isLoading, setIsLoading] = useState(true);
	const [bundleError, setBundleError] = useState("");

	const [traineeData, setTraineeData] = useState([]);
	const [technician, setTechnician] = useState({
		name: "N/A",
		site: "N/A",
		positionLevel: "N/A",
	});

	const [creditSummary, setCreditSummary] = useState({
		fundamental: 0,
		elective: 0,
		total: 0,
	});

	const [overallReady, setOverallReady] = useState({});
	const [readyLoading, setReadyLoading] = useState(true);

	const [competencyMap, setCompetencyMap] = useState([]);
	const [expanded, setExpanded] = useState({});
	const [activeTab, setActiveTab] = useState("all");
	const [searchCompCode, setSearchCompCode] = useState("");
	const [filteredData, setFilteredData] = useState([]);

	const [availableCourses, setAvailableCourses] = useState([]);
	const [availableLoading, setAvailableLoading] = useState(false);

	const [submittedWOs, setSubmittedWOs] = useState([]);
	const [submittedLoading, setSubmittedLoading] = useState(false);

	const [coreCompDetails, setCoreCompDetails] = useState("");
	const [showCoreModal, setShowCoreModal] = useState(false);

	const [failedWoDetails, setFailedWoDetails] = useState(null);
	const [showFailedWoModal, setShowFailedWoModal] = useState(false);

	const [workOrderDetails, setWorkOrderDetails] = useState([]);
	const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);

	const [showCreditHoursModal, setShowCreditHoursModal] = useState(false);
	const [creditHoursDetails, setCreditHoursDetails] = useState([]);
	const [creditHoursLoading, setCreditHoursLoading] = useState(false);

	const [documentUrls, setDocumentUrls] = useState([]);
	const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState(null);

	const [editingWoId, setEditingWoId] = useState(null);
	const [editCoreCompValue, setEditCoreCompValue] = useState("");

	const [autoSubmitting, setAutoSubmitting] = useState({});
	const [autoSubmitted, setAutoSubmitted] = useState({});

	const blank = (v) => v == null || String(v).trim() === "";

	const formatDate = (dateString) =>
		dateString ? String(dateString).split("T")[0] : "";

	const norm = (v) => (v == null ? "" : String(v)).trim().toUpperCase();

	const hasRealDate = (v) =>
		v != null &&
		String(v).trim() !== "" &&
		String(v).trim().toUpperCase() !== "NA";

	const isWorkdayCompleted = (record) =>
		norm(record?.etp_mstr_wd_status) === "COMPLETED";

	const toggleExpand = (enrollCode) => {
		setExpanded((e) => ({ ...e, [enrollCode]: !e[enrollCode] }));
	};

	const coreKey = (code) => (code || "").trim().toUpperCase();

	const fetchDocuments = async () => {
		try {
			const res = await fetch(`${API}/documents`, {
				headers: authHeaders,
			});
			if (!res.ok) throw new Error("Failed to fetch documents");
			const docs = await res.json();
			setDocumentUrls(Array.isArray(docs) ? docs : []);
		} catch (err) {
			console.error("Error fetching documents:", err);
			setDocumentUrls([]);
		}
	};

	const handleThumbnailClick = (document) => {
		setSelectedDocument(document);
		setIsDocumentModalOpen(true);
	};

	const handleCloseDocumentModal = () => {
		setIsDocumentModalOpen(false);
		setSelectedDocument(null);
	};

	useEffect(() => {
		fetch("/competency.json")
			.then((res) => res.json())
			.then((data) => setCompetencyMap(Array.isArray(data) ? data : []))
			.catch((err) => console.error("Failed to load competency.json:", err));
	}, []);

	const fetchBundle = async () => {
		if (!alias) return;

		setIsLoading(true);
		setBundleError("");
		setReadyLoading(true);

		try {
			const res = await fetch(
				`${API}/dashboard/trainee/${encodeURIComponent(
					alias,
				)}?trainerID=${encodeURIComponent(trainerID || "")}`,
				{
					headers: authHeaders,
				},
			);
			if (!res.ok) throw new Error(`Bundle failed (${res.status})`);

			const json = await res.json();

			const courses = json?.courses || [];
			setTraineeData(courses);

			setTechnician({
				name: json?.technician?.name || courses?.[0]?.etp_mstr_wd_name || "N/A",
				site: json?.technician?.site || courses?.[0]?.etp_mstr_site || "N/A",
				positionLevel:
					json?.technician?.positionLevel ||
					courses?.[0]?.position_level ||
					"N/A",
			});

			setCreditSummary({
				fundamental: Number(json?.creditHours?.fundamental || 0),
				elective: Number(json?.creditHours?.elective || 0),
				total: Number(json?.creditHours?.total || 0),
			});

			const progress = json?.progressByCoreComp || {};
			const map = {};

			Object.entries(progress).forEach(([code, val]) => {
				const k = coreKey(code);

				if (val && typeof val === "object") {
					if ("allMet" in val) {
						map[k] = {
							allMet: !!val.allMet,
							failingRule: val.failingRule || null,
							totalRequired: Number(
								val.totalRequired || val.totalRequiredDerived || 0,
							),
							passedCount: Number(val.passedCount || val.passCount || 0),
							counts: val.counts || val.current_progress || {},
							woRequired: val.woRequired !== false,
						};
					} else {
						const passedCount = Number(
							val.passCount || val.current_progress?.total_required || 0,
						);
						const totalRequired = Number(
							val.totalRequiredDerived || val.total_required || 0,
						);

						const inferredWoRequired = totalRequired > 0;

						const allMet =
							!!val.compliant ||
							(totalRequired > 0 ? passedCount >= totalRequired : true);

						map[k] = {
							allMet,
							failingRule: allMet ? null : "total_required",
							totalRequired,
							passedCount,
							counts: val.current_progress || {},
							woRequired: inferredWoRequired,
						};
					}
				}
			});

			setOverallReady(map);
		} catch (err) {
			console.error(err);
			setBundleError(err.message || "Failed to load trainee data");
			setTraineeData([]);
			setOverallReady({});
		} finally {
			setIsLoading(false);
			setReadyLoading(false);
		}
	};

	useEffect(() => {
		fetchBundle();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alias, trainerID]);

	useEffect(() => {
		if (!traineeData?.length) {
			setFilteredData([]);
			return;
		}
		const q = (searchCompCode || "").trim();
		if (!q) {
			setFilteredData(traineeData);
			return;
		}
		setFilteredData(
			traineeData.filter((r) => (r.etp_mstr_core_comp_code || "").includes(q)),
		);
	}, [searchCompCode, traineeData]);

	useEffect(() => {
		if (API && trainerID) fetchDocuments();
	}, [API, trainerID]);

	useEffect(() => {
		if (!traineeData) return;

		let data = [];

		const notStartedCodes = new Set();
		if (competencyMap?.length) {
			competencyMap.forEach((bucket) => {
				const codesInBucket = [
					bucket.enrollmentCourse,
					...(bucket.stages || []),
				];

				const bucketRows = traineeData.filter((r) =>
					codesInBucket.includes(r.etp_mstr_core_comp_code),
				);

				const hasNotStarted = bucketRows.some(
					(r) =>
						r.active_sts === "true" &&
						r.etp_mstr_wd_status === "Not Started" &&
						r.etp_mstr_wd_status !== "Excluded",
				);

				if (hasNotStarted) {
					bucketRows.forEach((r) =>
						notStartedCodes.add(r.etp_mstr_core_comp_code),
					);
				}
			});
		}

		if (activeTab === "available") {
			data = [...availableCourses];
		} else if (activeTab === "all") {
			data = traineeData.filter(
				(r) => r.active_sts === "true" && r.etp_mstr_wd_status !== "Excluded",
			);
		} else if (activeTab === "notStarted") {
			data =
				notStartedCodes.size > 0
					? traineeData.filter(
							(r) =>
								r.active_sts === "true" &&
								r.etp_mstr_wd_status !== "Excluded" &&
								notStartedCodes.has(r.etp_mstr_core_comp_code),
						)
					: traineeData.filter(
							(r) =>
								r.active_sts === "true" &&
								r.etp_mstr_wd_status === "Not Started" &&
								r.etp_mstr_wd_status !== "Excluded",
						);
		} else if (activeTab === "inProgress") {
			data = traineeData.filter((r) => {
				if (r.active_sts !== "true" || r.etp_mstr_wd_status === "Excluded")
					return false;

				if (notStartedCodes.has(r.etp_mstr_core_comp_code)) return false;

				const startHasValue = !blank(r.etp_mstr_start_date);
				const completionEmpty = blank(r.etp_mstr_completion_date);
				const wdInProgress = r.etp_mstr_wd_status === "In Progress";

				return wdInProgress || (startHasValue && completionEmpty);
			});
		} else if (activeTab === "completed") {
			data = traineeData.filter(
				(r) =>
					r.active_sts === "true" &&
					(!blank(r.etp_mstr_completion_date) ||
						coreKey(r.etp_mstr_course_status) === "COMPLETED") &&
					r.etp_mstr_wd_status !== "Excluded",
			);
		} else if (activeTab === "submitted") {
			data = [];
		}

		setFilteredData(data);
	}, [activeTab, traineeData, availableCourses, competencyMap]);

	const fetchAvailableCourses = async () => {
		if (!alias) return;
		setAvailableLoading(true);
		try {
			const res = await fetch(
				`${API}/etp-mstr/available-courses/${encodeURIComponent(alias)}`,
				{
					headers: authHeaders,
				},
			);
			if (!res.ok) throw new Error("Failed to fetch available courses");
			const data = await res.json();
			setAvailableCourses(Array.isArray(data) ? data : []);
		} catch (e) {
			console.error(e);
			setAvailableCourses([]);
		} finally {
			setAvailableLoading(false);
		}
	};

	useEffect(() => {
		if (
			activeTab === "available" &&
			availableCourses.length === 0 &&
			!availableLoading
		) {
			fetchAvailableCourses();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	const submittedReqSeqRef = useRef(0);

	const normalizeSubmitted = (payload) => {
		if (Array.isArray(payload)) return payload;
		if (payload && Array.isArray(payload.rows)) return payload.rows;
		if (payload && Array.isArray(payload.data)) return payload.data;
		if (payload && Array.isArray(payload.workOrders)) return payload.workOrders;
		if (payload && Array.isArray(payload.submittedWOs))
			return payload.submittedWOs;
		return [];
	};

	const fetchSubmittedWOs = async (aliasArg) => {
		if (!aliasArg) return;

		const seq = ++submittedReqSeqRef.current;

		setSubmittedLoading(true);
		try {
			const res = await fetch(
				`${API}/failed-wo/submitted/${encodeURIComponent(aliasArg)}`,
				{
					headers: authHeaders,
				},
			);
			if (!res.ok) throw new Error("Failed to fetch submitted WOs");

			const payload = await res.json();
			const list = normalizeSubmitted(payload);

			if (seq === submittedReqSeqRef.current) {
				setSubmittedWOs(list);
			}
		} catch (e) {
			console.error(e);
			setSubmittedWOs([]);
		} finally {
			setSubmittedLoading(false);
		}
	};

	useEffect(() => {
		if (activeTab === "submitted") {
			fetchSubmittedWOs(alias);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, alias, trainerID]);

	useEffect(() => {
		const target = location.state?.scrollTo;
		if (!target) return;

		const bucket = competencyMap.find(
			(c) => (c.stages || []).includes(target) || c.enrollmentCourse === target,
		);
		if (bucket) setExpanded((e) => ({ ...e, [bucket.enrollmentCourse]: true }));

		setTimeout(() => {
			document
				.querySelector(`[data-core='${target}']`)
				?.scrollIntoView({ behavior: "smooth" });
		}, 300);
	}, [location.state, competencyMap]);

	const fetchCoreCompDetails = async (coreCompCode) => {
		try {
			const response = await fetch(
				`${API}/etp-mstr/core-competencies/${encodeURIComponent(coreCompCode)}`,
				{
					headers: authHeaders,
				},
			);
			if (!response.ok)
				throw new Error("Failed to fetch core competency details");
			const data = await response.json();
			setCoreCompDetails(data.core_comp_details || "None");
			setShowCoreModal(true);
		} catch (error) {
			console.error("Error fetching core competency details:", error);
		}
	};

	const fetchCreditHoursDetails = async () => {
		setCreditHoursLoading(true);

		try {
			const response = await fetch(
				`${API}/etp-mstr/credit-hours-details/${encodeURIComponent(alias)}`,
				{
					headers: authHeaders,
				},
			);

			if (!response.ok) {
				throw new Error("Failed to fetch credit hours details");
			}

			const data = await response.json();

			setCreditHoursDetails(Array.isArray(data) ? data : []);
			setShowCreditHoursModal(true);
		} catch (error) {
			console.error("Error fetching credit hours details:", error);
		} finally {
			setCreditHoursLoading(false);
		}
	};

	const fetchWorkOrderDetails = async (aliasArg, coreCompCode) => {
		try {
			const response = await fetch(
				`${API}/failed-wo/work-orders/${encodeURIComponent(aliasArg)}/${encodeURIComponent(coreCompCode)}`,
				{
					headers: authHeaders,
				},
			);
			if (!response.ok) throw new Error("Failed to fetch work order details");
			const data = await response.json();
			setWorkOrderDetails(Array.isArray(data) ? data : []);
			setShowWorkOrderModal(true);
		} catch (error) {
			console.error("Error fetching work order details:", error);
		}
	};

	const handleFailedWoIconClick = async (etpMstrId) => {
		try {
			const response = await fetch(
				`${API}/etp-mstr/failed-wo-details/${encodeURIComponent(etpMstrId)}`,
				{
					headers: authHeaders,
				},
			);
			if (!response.ok)
				throw new Error("Failed to fetch failed work order details");
			const data = await response.json();
			setFailedWoDetails(data);
			setShowFailedWoModal(true);
		} catch (error) {
			console.error("Error fetching failed work order details:", error);
		}
	};

	const handleWoDetailsClick = (etpMstrId) => {
		const selected = traineeData.find((r) => r.etp_mstr_id === etpMstrId);
		if (!selected) return;

		const code = coreKey(selected.etp_mstr_core_comp_code);
		const creditWoPassed = overallReady?.[code]?.passedCount ?? 0;

		navigate(`/work-order/${alias}/${selected.etp_mstr_core_comp_code}`, {
			state: {
				courseTitle: selected.etp_mstr_core_comp || "N/A",
				courseCode: selected.etp_mstr_core_comp_code || "N/A",
				courseStatus: selected.etp_mstr_course_status || "N/A",
				courseType: selected.etp_mstr_comp_crit || "N/A",
				courseCredits: selected.etp_mstr_req_credit_hours || "N/A",
				courseReqWos: selected.etp_mstr_min_pass_wo || "N/A",
				courseStartDate: formatDate(selected.etp_mstr_start_date),
				courseCompletionDate: formatDate(selected.etp_mstr_completion_date),
				creditWoPassed,
			},
		});
	};

	const handleCoreCompSave = async (woId, newCoreComp) => {
		try {
			const res = await fetch(
				`${API}/failed-wo/update-core-comp/${encodeURIComponent(woId)}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						...authHeaders,
					},
					body: JSON.stringify({ newCoreComp }),
				},
			);
			if (!res.ok) throw new Error("Failed to update core comp");
			await fetchSubmittedWOs(alias);
			setEditingWoId(null);
		} catch (err) {
			console.error("Error updating core comp:", err);
		}
	};

	const autoCompleteCourse = async (etpMstrId) => {
		if (!etpMstrId || autoSubmitting[etpMstrId] || autoSubmitted[etpMstrId])
			return;

		setAutoSubmitting((p) => ({ ...p, [etpMstrId]: true }));

		try {
			const res = await fetch(
				`${API}/etp-mstr/auto-complete/${encodeURIComponent(etpMstrId)}`,
				{
					method: "PUT",
					headers: authHeaders,
				},
			);
			if (!res.ok) {
				const msg = await res.text().catch(() => "");
				throw new Error(msg || `Auto-complete failed (${res.status})`);
			}

			setAutoSubmitted((p) => ({ ...p, [etpMstrId]: true }));
			await fetchBundle();
		} catch (err) {
			console.error("Auto-complete error:", err);
		} finally {
			setAutoSubmitting((p) => ({ ...p, [etpMstrId]: false }));
		}
	};

	useEffect(() => {
		if (readyLoading) return;
		if (!traineeData || traineeData.length === 0) return;

		traineeData.forEach((record) => {
			const codeKey = coreKey(record.etp_mstr_core_comp_code);
			const readyVal = overallReady[codeKey];

			if (!readyVal?.allMet) return;
			if (coreKey(record.etp_mstr_course_status) === "COMPLETED") return;
			if (record.etp_mstr_completion_date) return;

			const isEC =
				(record.etp_mstr_core_comp_code || "").slice(-2).toUpperCase() === "EC";
			if (isEC) return;

			const code = coreKey(record.etp_mstr_core_comp_code);

			const bucket = competencyMap.find((b) => {
				const enroll = coreKey(b.enrollmentCourse);
				const stages = (b.stages || []).map((s) => coreKey(s));
				return enroll === code || stages.includes(code);
			});

			if (bucket) {
				const stages = (bucket.stages || []).map((s) => coreKey(s));
				const idx = stages.indexOf(code);
				const prevCode = idx > 0 ? stages[idx - 1] : null;

				const prevDone =
					!prevCode ||
					traineeData.some(
						(t) =>
							coreKey(t.etp_mstr_core_comp_code) === prevCode &&
							(coreKey(t.etp_mstr_course_status) === "COMPLETED" ||
								!blank(t.etp_mstr_completion_date)),
					);

				if (!prevDone) return;
			}

			const wdCompleted = isWorkdayCompleted(record);
			const wdDateOk = hasRealDate(record.wd_date_completed);
			const courseCompleted =
				coreKey(record.etp_mstr_course_status) === "COMPLETED" ||
				!blank(record.etp_mstr_completion_date);
			const workdayContentCompleted =
				courseCompleted || (wdCompleted && wdDateOk);
			if (!workdayContentCompleted) return;

			const tacValues = [
				record.tac_101_sts,
				record.tac_201_sts,
				record.tac_301_sts,
			].map((v) => (v || "NA").toUpperCase());
			const tacRequired = tacValues.every((v) => v === "NA") ? "No" : "Yes";
			const tacCompleted = tacValues.includes("YES");
			if (tacRequired === "Yes" && !tacCompleted) return;

			autoCompleteCourse(record.etp_mstr_id);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		readyLoading,
		overallReady,
		traineeData,
		competencyMap,
		autoSubmitting,
		autoSubmitted,
	]);

	const bucketRows = useMemo(() => {
		if (!competencyMap?.length) return [];
		if (!filteredData?.length) return [];

		return competencyMap
			.map((comp) => {
				const enrollCode = comp.enrollmentCourse;
				const rows = filteredData.filter(
					(r) =>
						(comp.stages || []).includes(r.etp_mstr_core_comp_code) ||
						r.etp_mstr_core_comp_code === enrollCode,
				);
				if (rows.length === 0) return null;
				return { comp, enrollCode, rows };
			})
			.filter(Boolean);
	}, [competencyMap, filteredData]);

	if (isLoading) return <LoadingComponent />;

	if (bundleError) {
		return (
			<div className="details-page">
				<div className="page-shell">
					<div className="topbar">
						<button className="btn btn-light" onClick={() => navigate(-1)}>
							&larr; Back
						</button>
						<button className="btn btn-primary" onClick={fetchBundle}>
							Retry
						</button>
					</div>

					<div className="alert-box">
						<div className="alert-title">Could not load trainee data</div>
						<div className="alert-text">{bundleError}</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="details-page">
			<div className="page-shell">
				<div className="topbar">
					<button className="btn btn-light" onClick={() => navigate(-1)}>
						&larr; Back
					</button>

					<div className="topbar-right">
						<input
							className="search-input"
							placeholder="Search course code…"
							value={searchCompCode}
							onChange={(e) => setSearchCompCode(e.target.value)}
						/>
						<button className="btn btn-outline-primary" onClick={fetchBundle}>
							Refresh
						</button>
					</div>
				</div>

				{documentUrls.length > 0 && (
					<section className="hp__panel">
						<div className="hp__panelHeader">
							<h2 className="hp__title">Information Center</h2>
							<div className="hp__subtitle">
								Click a slide to open the full document.
							</div>
						</div>

						<div className="hp__carousel">
							<Carousel interval={4500}>
								{documentUrls.map((doc, index) => (
									<Carousel.Item
										key={index}
										onClick={() => handleThumbnailClick(doc)}
									>
										<div className="hp__thumbWrap">
											<img
												className="d-block w-100"
												src={doc.thumbnail}
												alt={`Slide ${index}`}
											/>
										</div>
									</Carousel.Item>
								))}
							</Carousel>
						</div>
					</section>
				)}

				<div className="summary-grid">
					<div className="summary-card">
						<div className="summary-title">Technician</div>
						<div className="summary-row">
							<span className="label">Name</span>
							<span className="value">{technician.name}</span>
						</div>
						<div className="summary-row">
							<span className="label">Site</span>
							<span className="value">{technician.site}</span>
						</div>
						<div className="summary-row">
							<span className="label">Position Level</span>
							<span className="value">{technician.positionLevel}</span>
						</div>
					</div>
					<button
						className="summary-card clickable"
						onClick={fetchCreditHoursDetails}
					>
						<div className="summary-title">Credit Hours</div>
						<div className="credit-line">
							<span>Fundamental</span>
							<strong>{creditSummary.fundamental}</strong>
						</div>
						<div className="credit-line">
							<span>Elective</span>
							<strong>{creditSummary.elective}</strong>
						</div>
						<div className="credit-total">
							<span>Total</span>
							<strong>{creditSummary.total}</strong>
						</div>
						{creditHoursLoading && <div className="mini-loading">Loading…</div>}
					</button>
				</div>

				<div className="tabs">
					{[
						["all", "All Courses"],
						["notStarted", "Not Started"],
						["inProgress", "In Progress"],
						["completed", "Completed"],
						["available", "Available"],
						["submitted", "Work Orders Submitted"],
					].map(([key, label]) => (
						<button
							key={key}
							className={`tab-btn ${activeTab === key ? "active" : ""}`}
							onClick={() => setActiveTab(key)}
						>
							{label}
						</button>
					))}
				</div>

				{activeTab === "submitted" && (
					<div className="table-shell">
						{submittedLoading ? (
							<LoadingComponent />
						) : (
							<table className="table-modern">
								<thead>
									<tr>
										<th>WO #</th>
										<th>Comp Date</th>
										<th>Site</th>
										<th>Template</th>
										<th>Core Comp</th>
										<th>Status</th>
										<th>Duplicate</th>
										<th>Type</th>
									</tr>
								</thead>
								<tbody>
									{submittedWOs.length > 0 ? (
										submittedWOs.map((wo, idx) => (
											<tr
												key={`${wo.bert_wo_dtls_id ?? "noid"}-${wo.wo_num ?? "nowo"}-${wo.wo_comp_date ?? "nodate"}-${idx}`}
											>
												<td>{wo.wo_num}</td>
												<td>{formatDate(wo.wo_comp_date)}</td>
												<td>{wo.site_id}</td>
												<td>{wo.template}</td>
												<td>
													{isAdmin ? (
														editingWoId === wo.bert_wo_dtls_id ? (
															<div className="inline-edit">
																<input
																	type="text"
																	value={editCoreCompValue}
																	onChange={(e) =>
																		setEditCoreCompValue(e.target.value)
																	}
																	className="inline-input"
																/>
																<button
																	className="btn btn-success btn-sm"
																	onClick={() =>
																		handleCoreCompSave(
																			wo.bert_wo_dtls_id,
																			editCoreCompValue,
																		)
																	}
																>
																	Save
																</button>
																<button
																	className="btn btn-secondary btn-sm"
																	onClick={() => setEditingWoId(null)}
																>
																	Cancel
																</button>
															</div>
														) : (
															<button
																className="linklike"
																onClick={() => {
																	setEditingWoId(wo.bert_wo_dtls_id);
																	setEditCoreCompValue(wo.core_comp || "");
																}}
															>
																{wo.core_comp || "(none)"}
															</button>
														)
													) : (
														wo.core_comp
													)}
												</td>
												<td>{wo.status}</td>
												<td>{wo.duplicate_wo}</td>
												<td>{wo.Type}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={8} className="empty-row">
												None submitted
											</td>
										</tr>
									)}
								</tbody>
							</table>
						)}
					</div>
				)}

				{activeTab !== "submitted" && (
					<div className="table-shell">
						<table className="table-modern">
							<thead>
								<tr>
									<th>Core Competency</th>
									<th>Course Code</th>
									<th>Course Type</th>
									<th>Credit Hours</th>

									{activeTab === "available" ? (
										<th className="center">Status</th>
									) : (
										<>
											<th>Minimum Work Orders</th>
											<th>Workday Course Content</th>
											<th>Course Start Date</th>
											<th>Course Completion Date</th>
											<th>Approved By</th>
											<th>TAC Required</th>
											<th>TAC Status</th>
											<th className="center">Action</th>
										</>
									)}
								</tr>
							</thead>

							{activeTab === "available" ? (
								<tbody>
									{availableLoading ? (
										<tr>
											<td colSpan={5} className="empty-row">
												Loading…
											</td>
										</tr>
									) : (
										availableCourses.map((record, index) => {
											const isUnderDev =
												record.LearningMethod === "Under Development";
											return (
												<tr
													key={index}
													className={isUnderDev ? "under-development" : ""}
												>
													<td>
														{record.CoreCompetency || record.etp_mstr_core_comp}
													</td>
													<td>
														{record.UniqueCode ||
															record.etp_mstr_core_comp_code}
													</td>
													<td>
														{record.FundamentalOrElective ||
															record.etp_mstr_comp_crit}
													</td>
													<td>
														{record.CreditHours ||
															record.etp_mstr_req_credit_hours}
													</td>
													<td className="center">
														{record.LearningMethod === "Under Development"
															? "Under Development"
															: "Available"}
													</td>
												</tr>
											);
										})
									)}
								</tbody>
							) : (
								<tbody>
									{bucketRows.map(({ comp, enrollCode, rows }) => {
										const isOpen = !!expanded[enrollCode];

										return (
											<Fragment key={enrollCode}>
												<tr className="bucket-row">
													<td colSpan={12}>
														<div className="bucket-row-inner">
															<div className="bucket-left">
																<i
																	className={`bi ${
																		isOpen
																			? "bi-chevron-down"
																			: "bi-chevron-right"
																	}`}
																	onClick={() => toggleExpand(enrollCode)}
																	role="button"
																	aria-label="toggle"
																/>
																<strong>
																	{comp.category} ({enrollCode})
																</strong>
															</div>

															<div className="bucket-right"></div>
														</div>
													</td>
												</tr>

												{isOpen &&
													rows.map((record, idx) => {
														const tacValues = [
															record.tac_101_sts,
															record.tac_201_sts,
															record.tac_301_sts,
														].map((v) => (v || "NA").toUpperCase());
														const tacRequired = tacValues.every(
															(v) => v === "NA",
														)
															? "No"
															: "Yes";
														let tacStatus = "Unknown";
														if (tacRequired === "No")
															tacStatus = "Not Required";
														else if (tacValues.includes("YES"))
															tacStatus = "Completed";
														else if (tacValues.includes("NO"))
															tacStatus = "Not Completed";
														const tacCompleted = tacValues.includes("YES");
														const tacOk = tacRequired === "No" || tacCompleted;

														const myIndex = (comp.stages || []).indexOf(
															record.etp_mstr_core_comp_code,
														);
														const prevCode =
															myIndex > 0 ? comp.stages[myIndex - 1] : null;
														const prevDone =
															!prevCode ||
															traineeData.some(
																(t) =>
																	t.etp_mstr_core_comp_code === prevCode &&
																	((t.etp_mstr_course_status || "") ===
																		"Completed" ||
																		!blank(t.etp_mstr_completion_date)),
															);

														const codeKey = coreKey(
															record.etp_mstr_core_comp_code,
														);

														const readyVal = overallReady[codeKey] || {
															allMet: false,
															failingRule: "total_required",
															woRequired: true,
															totalRequired: 0,
															passedCount: 0,
														};

														const stillChecking = readyLoading;
														const woRequired = readyVal?.woRequired !== false;
														const woReady = woRequired
															? !!readyVal?.allMet
															: true;

														const failingRule = readyVal?.failingRule;

														const isAlreadyCompleted =
															coreKey(record.etp_mstr_course_status) ===
																"COMPLETED" ||
															!blank(record.etp_mstr_completion_date);

														const wdCompleted = isWorkdayCompleted(record);
														const wdDateOk = hasRealDate(
															record.wd_date_completed,
														);
														const courseCompleted =
															coreKey(record.etp_mstr_course_status) ===
																"COMPLETED" ||
															!blank(record.etp_mstr_completion_date);
														const workdayContentCompleted =
															courseCompleted || (wdCompleted && wdDateOk);

														const isEC =
															(record.etp_mstr_core_comp_code || "")
																.slice(-2)
																.toUpperCase() === "EC";

														const eligibleForAutoComplete =
															!stillChecking &&
															woReady &&
															!isAlreadyCompleted &&
															!isEC &&
															prevDone &&
															workdayContentCompleted &&
															tacOk;

														let disableReason = "";
														if (isAlreadyCompleted) disableReason = "Completed";
														else if (!prevDone)
															disableReason = `Finish ${prevCode}`;
														else if (stillChecking) disableReason = "Checking…";
														else if (isEC) disableReason = "Finish via Workday";
														else if (!woReady) {
															if (!woRequired) {
																disableReason = "No work orders required";
															} else if (
																failingRule === "missing_requirements"
															) {
																disableReason = "Requirements config missing";
															} else if (failingRule === "total_required") {
																const totalReq = readyVal?.totalRequired || 0;
																const passed = readyVal?.passedCount ?? 0;
																disableReason =
																	totalReq > 0
																		? `Need Total WOs (${passed}/${totalReq})`
																		: "Need Total WOs";
															} else if (
																failingRule?.toLowerCase().includes("max")
															) {
																disableReason = `Over Max (${failingRule})`;
															} else if (
																failingRule?.toLowerCase().includes("min")
															) {
																disableReason = `Missing ${failingRule}`;
															} else {
																disableReason = "WO Requirements";
															}
														} else if (!workdayContentCompleted) {
															disableReason = "Finish in Workday";
														} else if (!tacOk) {
															disableReason = "Complete TAC";
														} else {
															disableReason = "Not ready";
														}

														let label = "Not ready";
														if (isAlreadyCompleted) label = "Completed";
														else if (autoSubmitting[record.etp_mstr_id])
															label = "Auto-submitting…";
														else if (eligibleForAutoComplete)
															label = "Ready (auto-complete)";
														else label = disableReason || "Not ready";

														return (
															<tr
																key={`${enrollCode}-${idx}`}
																data-core={record.etp_mstr_core_comp_code}
																className={[
																	record.LearningMethod === "Under Development"
																		? "under-development"
																		: "",
																]
																	.filter(Boolean)
																	.join(" ")}
															>
																<td className="cell-with-icon">
																	<button
																		className="icon-chip"
																		onClick={() =>
																			handleWoDetailsClick(record.etp_mstr_id)
																		}
																		aria-label="work order details"
																	>
																		<i className="bi bi-search" />
																	</button>
																	{record.CoreCompetency ||
																		record.etp_mstr_core_comp}
																</td>

																<td className="cell-with-icon">
																	<button
																		className="icon-chip"
																		onClick={() =>
																			fetchCoreCompDetails(
																				record.etp_mstr_core_comp_code,
																			)
																		}
																		aria-label="core competency info"
																	>
																		<i className="bi bi-info-circle" />
																	</button>
																	{record.UniqueCode ||
																		record.etp_mstr_core_comp_code}
																</td>

																<td>{record.etp_mstr_comp_crit}</td>
																<td>{record.etp_mstr_req_credit_hours}</td>
																<td>{record.etp_mstr_min_pass_wo}</td>

																<td>
																	{workdayContentCompleted
																		? "Completed"
																		: "Finish in Workday"}
																</td>

																<td>
																	{formatDate(record.etp_mstr_start_date)}
																</td>
																<td>
																	{formatDate(record.etp_mstr_completion_date)}
																</td>
																<td>{record.etp_mstr_trainer_approved}</td>
																<td>{tacRequired}</td>
																<td>{tacStatus}</td>

																<td className="center">
																	{isEC ? (
																		<button
																			className="btn btn-secondary btn-sm"
																			disabled
																		>
																			Finish via Workday
																		</button>
																	) : (
																		<button
																			className={`btn btn-sm ${
																				!workdayContentCompleted
																					? "btn-outline-secondary"
																					: eligibleForAutoComplete
																						? "btn-dark"
																						: "btn-outline-secondary"
																			}`}
																			disabled
																			title={disableReason}
																		>
																			{label}
																		</button>
																	)}
																</td>
															</tr>
														);
													})}
											</Fragment>
										);
									})}

									{bucketRows.length === 0 && (
										<tr>
											<td colSpan={12} className="empty-row">
												No courses found.
											</td>
										</tr>
									)}
								</tbody>
							)}
						</table>
					</div>
				)}

				{showCoreModal && (
					<CoreCompetencyModal
						details={coreCompDetails}
						onClose={() => setShowCoreModal(false)}
					/>
				)}

				{showFailedWoModal && (
					<FailedWoDetailsModal
						details={failedWoDetails}
						onClose={() => setShowFailedWoModal(false)}
					/>
				)}

				{showWorkOrderModal && (
					<WorkOrderDetailsModal
						isOpen={showWorkOrderModal}
						onClose={() => setShowWorkOrderModal(false)}
						workOrders={workOrderDetails}
					/>
				)}

				{showCreditHoursModal && (
					<CreditHoursModal
						isOpen={showCreditHoursModal}
						onClose={() => setShowCreditHoursModal(false)}
						details={creditHoursDetails}
					/>
				)}

				<Modal
					show={isDocumentModalOpen}
					onHide={handleCloseDocumentModal}
					size="xl"
					centered
					backdrop
					keyboard
				>
					<Modal.Header closeButton>
						<Modal.Title>Document Viewer</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{selectedDocument && (
							<iframe
								src={selectedDocument.url}
								title="Document Viewer"
								style={{ width: "100%", height: "75vh", border: "none" }}
								sandbox="allow-scripts allow-same-origin"
							/>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseDocumentModal}>
							Close
						</Button>
					</Modal.Footer>
				</Modal>
			</div>
		</div>
	);
};

export default DetailsPage;
